import {
  isToday,
  isYesterday,
  isTomorrow,
  differenceInDays,
  differenceInCalendarDays,
  isSameYear,
  format,
  isBefore,
  parseISO,
  isValid,
  getTime,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { MessageContentType } from "@superagents/types/api/entities";
import { DateTimeValue } from "@superagents/types/api";

/**
 * Formata a data como um badge de dia simples:
 * Hoje | Ontem | <Nome do dia da semana> | dd/MM/yyyy
 */
export const formatDateAsBadge = (toFormat: string | Date): string => {
  const date = typeof toFormat === "string" ? new Date(toFormat) : toFormat;

  if (isToday(date)) {
    return "Hoje";
  }

  if (isYesterday(date)) {
    return "Ontem";
  }

  const daysDiff = differenceInDays(new Date(), date);
  if (daysDiff < 7) {
    const dayName = format(date, "EEEE", { locale: ptBR });
    return dayName.charAt(0).toUpperCase() + dayName.slice(1);
  }

  // Formato padrão dia/mês/ano
  return format(date, "P", { locale: ptBR });
};

/**
 * Formata a data como última mensagem:
 * HH:mm | Ontem | <Nome do dia da semana> | dd/MM/yyyy
 */
export const formatDateAsLastMessage = (
  toFormat: DateTimeValue | null | undefined
) => {
  if (!toFormat) return "";

  const date = typeof toFormat === "string" ? new Date(toFormat) : toFormat;

  if (isToday(date)) {
    return format(date, "HH:mm");
  }

  if (isYesterday(date)) {
    return "Ontem";
  }

  const daysDiff = differenceInDays(new Date(), date);
  if (daysDiff < 7) {
    const dayName = format(date, "EEEE", { locale: ptBR });
    return dayName.charAt(0).toUpperCase() + dayName.slice(1);
  }

  return format(date, "P", { locale: ptBR });
};

const EXPIRED_MESSAGES = {
  image: "Esta imagem expirou.",
  document: "Este documento expirou.",
  audio: "Este áudio expirou.",
};

/**
 * Retorna rótulo e flag de expiração:
 * "Disponível até hoje às HH:mm" | "Disponível até amanhã às HH:mm" |
 * "Disponível até <dia da semana> às HH:mm" (até 6 dias) |
 * "Disponível até dd/MM às HH:mm" (dd/MM/aaaa em outro ano) ou
 * "Esta imagem expirou."
 */
export function formatAvailableUntil(
  time: Date | string | number,
  type: MessageContentType
): {
  label: string;
  expired: boolean;
} {
  const date =
    typeof time === "number" ? new Date(time) : new Date(time.toString());
  const now = new Date();

  if (isBefore(date, now)) {
    return {
      label:
        type === "image" || type === "document" || type === "audio"
          ? EXPIRED_MESSAGES[type]
          : "Este conteúdo expirou.",
      expired: true,
    };
  }

  let dayLabel: string;
  if (isToday(date)) {
    dayLabel = "hoje";
  } else if (isTomorrow(date)) {
    dayLabel = "amanhã";
  } else if (differenceInCalendarDays(date, now) < 7) {
    const weekday = format(date, "EEEE", { locale: ptBR });
    dayLabel = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  } else {
    // SA-1433: com 90 dias o prazo fica semanas à frente; só o dia da semana
    // daria a entender que a mídia some nesta semana.
    dayLabel = format(date, isSameYear(date, now) ? "dd/MM" : "dd/MM/yyyy");
  }

  const timeLabel = format(date, "HH:mm");
  return {
    label: `Disponível até ${dayLabel} às ${timeLabel}`,
    expired: false,
  };
}

/**
 * Obtém o timestamp (milissegundos desde a epoch) de forma segura a partir de uma data.
 * Lida com strings no formato ISO, objetos Date, números, null ou undefined.
 * Retorna 0 para entradas inválidas ou nulas.
 *
 * @param date A data para processar, pode ser uma string ISO, um objeto Date ou um número.
 * @returns O timestamp como um número, ou 0 se a data for inválida.
 */
export const getTimestamp = (
  date: DateTimeValue | null | undefined
): number => {
  if (!date) {
    return 0;
  }

  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === "string") {
    dateObj = parseISO(date);
  } else {
    dateObj = new Date(date);
  }

  if (!isValid(dateObj)) {
    return 0;
  }

  return getTime(dateObj);
};
